import { ExternalLink, Layers, X } from 'lucide-react';

import {
  permissions as permifyPermissions,
  stageNames,
} from '@repo/api/permify/constants';
import { ExportCandidates } from '@repo/ui/components/custom/candidate/export-candidates/component';
import {
  type NavigationBarBulkActionsConfig,
  Tab,
} from '@repo/ui/components/custom/navigation-bar/types';
import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { showSuccessToast } from '@repo/ui/components/ui/success-toast';
import { useApplyViewPermissions } from '@repo/ui/hooks';
import { cn } from '@repo/ui/lib/utils';
import { SelectionMode, useViewPermissions, ViewMode } from '@repo/ui/state';
import { DismissCandidate, MoveCandidateToStage } from '../../';
import { useCandidates, useCandidateSelection, useOpenCall } from '@/state';
import { Button, buttonVariants } from '../../../ui/button';
import { useState } from 'react';

type NavigationBarActions = {
  onSelectClick: () => void;
};

export type BulkNavigationActionsProps = {
  activeTab: string;
  bulkActionsConfig?: NavigationBarBulkActionsConfig;
  error?: string | null;
  loading?: boolean;
  tabs: Tab[];
  viewMode: ViewMode;
  isJuryStage: boolean;
} & NavigationBarActions;

const defaultBulkActionsConfig: NavigationBarBulkActionsConfig = {
  contactExport: true,
  copilotExport: true,
  dismiss: true,
  moveStage: true,
};

const BulkNavigationActions: React.FC<BulkNavigationActionsProps> = ({
  isJuryStage,
  activeTab,
  bulkActionsConfig,
  error,
  loading,
  onSelectClick,
  tabs,
}) => {
  // const { p }  =  useCandidates();
  const {
    exportCandidatesToCopilot,
    params: {
      filters: { openCallId, stageId },
    },
  } = useCandidates();

  const { openCall: currentOpenCall } = useOpenCall(openCallId!);
  const resolvedBulkActionsConfig = {
    ...defaultBulkActionsConfig,
    ...bulkActionsConfig,
  };
  const { data: permissions } = useViewPermissions();
  const uuid = () =>
    Date.now().toString(36) + Math.random().toString(36).substring(2);
  useApplyViewPermissions(permissions);
  const { toggleSelectAll, selectionMode, selectedIds } =
    useCandidateSelection();
  const toolBarButtonStylesWide =
    'border-input flex items-center space-x-2 !p-2 border';
  const activeTabName = tabs.find((tab) => tab.id === activeTab)?.name || '';
  const isFinalistsStage = activeTabName === stageNames.finalists;
  const [moveToId, setMoveToId] = useState(uuid());
  return (
    <div className="flex w-full pl-1 items-center space-x-0 h-24 md:h-12 border-none justify-end gap-2 flex-col-reverse md:flex-row">
      <div className="flex w-full pl-1 items-center space-x-0 h-24 md:h-12 justify-start md:justify-end gap-2">
        {resolvedBulkActionsConfig.moveStage && (
          <MoveCandidateToStage
            onMoveCompleted={() => setMoveToId(uuid())}
            key={`move-to-${activeTab}-${moveToId}`}
            candidateIds={selectedIds}
            variant="ghost"
            className="border-input flex items-center space-x-2 p-2 border"
          />
        )}
        {resolvedBulkActionsConfig.dismiss &&
          ![stageNames.jury, stageNames.dismissed].includes(activeTabName) &&
          stageId !== '0' && (
            <DismissCandidate
              candidateIds={selectedIds}
              className={toolBarButtonStylesWide}
              variant="ghost"
              showIcon
            />
          )}
        {resolvedBulkActionsConfig.contactExport && (
          <ExportCandidates
            variant="ghost"
            className={toolBarButtonStylesWide}
            candidateIds={selectedIds}
          />
        )}
        {isFinalistsStage && resolvedBulkActionsConfig.copilotExport && (
          <Button
            variant="ghost"
            disabled={selectedIds.length === 0}
            aria-disabled={selectedIds.length === 0}
            data-permission={permifyPermissions.openCalls.exportCandidates}
            onClick={handleExportToCopilot}
            className={cn(
              toolBarButtonStylesWide,
              buttonVariants({ variant: 'ghost' }),
            )}
          >
            Export to Copilot
            <ExternalLink />
          </Button>
        )}
      </div>
      <div className="flex md:w-auto w-full pl-1 items-center space-x-0 h-12 justify-between md:justify-end md:gap-2 border-b-2 md:border-none pb-2 md:pb-0">
        {!isJuryStage && (
          <Button
            data-permission={permifyPermissions.openCalls.bulkAction}
            className={toolBarButtonStylesWide}
            disabled={loading || !!error}
            onClick={toggleSelectAll}
          >
            {selectionMode === SelectionMode.DESELECT_ALL
              ? 'Select All'
              : 'Deselect All'}
            <Layers />
          </Button>
        )}
        <Button
          variant="ghost"
          data-permission={permifyPermissions.openCalls.bulkAction}
          className="border-input flex items-center space-x-2 !p-2 md:border"
          disabled={loading || !!error}
          onClick={onSelectClick}
          data-testid="to-stage-view"
        >
          <span className="px-[1px]">
            <X />
          </span>
        </Button>
      </div>
    </div>
  );

  async function handleExportToCopilot() {
    const errorTitle = 'Unable to export to Copilot';

    if (!currentOpenCall) {
      showErrorToast({
        title: errorTitle,
        description: 'Open call details are unavailable.',
      });
      return;
    }

    // Limiting export to 30 finalist to be on the safe side regarding resource limitations
    // on backend side. TODO: We should also add this constraint at the backend API meanwhile we find
    // a way to optimize this operation that will allow us to remove this limitation.
    if (selectedIds?.length > 30) {
      showErrorToast({
        title: errorTitle,
        description:
          'You can only export a maximum of 30 candidates to Copilot at a time.',
      });
      return;
    }

    const response = await exportCandidatesToCopilot(
      selectedIds,
      currentOpenCall,
    );
    if (response && 'visualStoryURL' in response) {
      showSuccessToast({
        title: 'Successfully exported finalists',
        description: 'Redirecting to Copilot...',
      });
      window.open(response.visualStoryURL, '_blank');
    } else {
      console.error('Unable to fetch opencall', currentOpenCall, openCallId);
      showErrorToast({
        title: errorTitle,
        description: (response as unknown as Error)?.message,
      });
    }
  }
};

export default BulkNavigationActions;
